/**
 * audioEngine — plain-module playback for the three audio channels.
 *
 *   playSfx(key, volume)                  one-shot clip, plays immediately
 *   setBackground(bg, { fadeMs })        looping track; crossfades between
 *                                        tracks, fades out on bg = null
 *   setVideoAudio(va, { clockOffset })   the audio track of the video the
 *                                        walls show muted — an <audio> on the
 *                                        same file, held to the same shared
 *                                        clock epoch as the video frames
 *   Background objects: { key, volume } (volume 0..1, default 0.5)
 *   VideoAudio objects: { url, syncEpoch, playbackRate, loop, volume }
 *
 * Browser autoplay: if playback is blocked (no prior user gesture on this
 * screen), the engine reports it via onBlocked and remembers the desired
 * channels so retryBlocked() can resume after a click.
 */
import { SFX, BACKGROUNDS } from './sfxManifest';
import { startVideoClockSync } from '../components/Media/videoClockSync';

const FADE_MS = 2000;
const current = { audio: null, key: null, targetVolume: 0.5 };
const videoCh = { audio: null, url: null, stopSync: null };
const ambientCh = { audio: null, url: null };
let wantedBackground; // remembered while autoplay-blocked
let wantedVideoAudio;
let wantedAmbient;
let blockedCallback = null;

export function onBlocked(cb) { blockedCallback = cb; }

function reportBlocked(err) {
  if (err?.name === 'NotAllowedError') blockedCallback?.();
  else console.error('[audioEngine] playback failed:', err);
}

export function playSfx(key, volume = 1) {
  const clip = SFX[key];
  if (!clip) { console.warn(`[audioEngine] unknown sfx "${key}"`); return; }
  const audio = new Audio(clip.file);
  audio.volume = Math.max(0, Math.min(1, volume));
  audio.play().catch(reportBlocked);
}

/* Each audio element carries its own fade timer so a crossfade can run the
   outgoing fade-out and incoming fade-in concurrently. */
function fadeTo(audio, target, ms, done) {
  clearInterval(audio._fadeTimer);
  const stepMs = 50;
  const step = (target - audio.volume) / Math.max(1, ms / stepMs);
  audio._fadeTimer = setInterval(() => {
    const next = audio.volume + step;
    const arrived = step >= 0 ? next >= target : next <= target;
    audio.volume = arrived ? target : Math.max(0, Math.min(1, next));
    if (arrived) { clearInterval(audio._fadeTimer); done?.(); }
  }, stepMs);
}

/** Fade the running loop out and stop it. */
function fadeOutAndStop(audio, ms) {
  fadeTo(audio, 0, ms, () => { audio.pause(); audio.src = ''; });
}

export function setBackground(bg, { fadeMs = FADE_MS } = {}) {
  wantedBackground = bg;
  const key = bg?.key || null;
  const volume = Math.max(0, Math.min(1, bg?.volume ?? 0.5));

  if (key === current.key && current.audio) {
    current.targetVolume = volume;
    fadeTo(current.audio, volume, 400);
    return;
  }

  if (current.audio) fadeOutAndStop(current.audio, fadeMs);
  current.audio = null;
  current.key = null;
  if (!key) return;

  const track = BACKGROUNDS[key];
  if (!track) { console.warn(`[audioEngine] unknown background "${key}"`); return; }

  const audio = new Audio(track.file);
  audio.loop = true;
  audio.volume = 0;
  current.audio = audio;
  current.key = key;
  current.targetVolume = volume;
  audio.play()
    .then(() => fadeTo(audio, volume, fadeMs))
    .catch((err) => { current.audio = null; current.key = null; reportBlocked(err); });
}

export function setVideoAudio(va, { clockOffset = 0 } = {}) {
  wantedVideoAudio = { va, clockOffset };
  const url = va?.url || null;

  if (url === videoCh.url) {
    if (videoCh.audio) fadeTo(videoCh.audio, Math.max(0, Math.min(1, va?.volume ?? 1)), 400);
    return;
  }

  videoCh.stopSync?.();
  videoCh.stopSync = null;
  if (videoCh.audio) fadeOutAndStop(videoCh.audio, 400);
  videoCh.audio = null;
  videoCh.url = null;
  if (!url) return;

  const audio = new Audio(url);
  audio.crossOrigin = 'anonymous';
  audio.loop = !!va.loop;
  audio.volume = 0;
  videoCh.audio = audio;
  videoCh.url = url;
  audio.play()
    .then(() => {
      // Short fade-in avoids a pop when joining the video mid-timeline; the
      // clock sync snaps currentTime to where the (muted) walls already are
      fadeTo(audio, Math.max(0, Math.min(1, va.volume ?? 1)), 400);
      videoCh.stopSync = startVideoClockSync(audio, {
        syncEpoch: va.syncEpoch,
        clockOffset,
        playbackRate: va.playbackRate || 1,
      });
      if (va.playbackRate) audio.playbackRate = va.playbackRate;
    })
    .catch((err) => { videoCh.audio = null; videoCh.url = null; reportBlocked(err); });
}

/**
 * Ambient channel — this screen's stem of the room-wide soundscape.
 * One looping stem per screen; long fades so scape changes wash over.
 */
export function setAmbient(stemUrl, { volume = 0.5, fadeMs = 3000 } = {}) {
  wantedAmbient = { stemUrl, volume };
  const url = stemUrl || null;

  if (url === ambientCh.url) {
    if (ambientCh.audio) fadeTo(ambientCh.audio, Math.max(0, Math.min(1, volume)), 600);
    return;
  }

  if (ambientCh.audio) fadeOutAndStop(ambientCh.audio, fadeMs);
  ambientCh.audio = null;
  ambientCh.url = null;
  if (!url) return;

  const audio = new Audio(url);
  audio.loop = true;
  audio.volume = 0;
  ambientCh.audio = audio;
  ambientCh.url = url;
  audio.play()
    .then(() => fadeTo(audio, Math.max(0, Math.min(1, volume)), fadeMs))
    .catch((err) => { ambientCh.audio = null; ambientCh.url = null; reportBlocked(err); });
}

/** Re-attempt the remembered channels after a user gesture unlocked audio. */
export function retryBlocked() {
  if (wantedBackground !== undefined) setBackground(wantedBackground);
  if (wantedVideoAudio !== undefined) setVideoAudio(wantedVideoAudio.va, { clockOffset: wantedVideoAudio.clockOffset });
  if (wantedAmbient !== undefined) setAmbient(wantedAmbient.stemUrl, { volume: wantedAmbient.volume });
}
